import React from 'react';
import { Box, Button, Card, Chip, FormControl, FormLabel, Input, Option, Select, Stack, Textarea, Typography, Slider, Table, Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Divider, Sheet, Radio, RadioGroup, Switch, Checkbox, Autocomplete, Tabs, TabList, Tab } from '@mui/joy';
import type { DocumentKind, DocumentLineInput, DocumentFeesConfig } from '../types/documents.types';
import type { Customer } from '../../../services/customerService';

// Shared props interface for all wizard components
export interface DocumentWizardSharedProps {
  // Document data
  kind: DocumentKind;
  setKind: (kind: DocumentKind) => void;
  lifecycleRef: string;
  setLifecycleRef: (ref: string) => void;
  title: string;
  setTitle: (title: string) => void;
  description: string;
  setDescription: (description: string) => void;
  notes: string;
  setNotes: (notes: string) => void;
  fees: DocumentFeesConfig;
  setFees: (fees: DocumentFeesConfig) => void;
  
  // Customer data
  selectedCustomer: Customer | null;
  setSelectedCustomer: (customer: Customer | null) => void;
  customerTitle: string;
  setCustomerTitle: (title: string) => void;
  
  // Deposit data
  depositAmount: number;
  setDepositAmount: (amount: number) => void;
  depositPct: number;
  setDepositPct: (pct: number) => void;
  depositMode: 'percent' | 'fixed';
  setDepositMode: (mode: 'percent' | 'fixed') => void;
  depositEnabled: boolean;
  setDepositEnabled: (enabled: boolean) => void;
  depositFixed: number;
  setDepositFixed: (fixed: number) => void;

  // Payment structure metadata (UI-only helper; does not affect totals)
  paymentStructureApplicable: boolean;
  setPaymentStructureApplicable: (enabled: boolean) => void;
  paymentStructurePattern: string | null;
  setPaymentStructurePattern: (pattern: string | null) => void;
  
  // VAT data
  vatEnabled: boolean;
  setVatEnabled: (enabled: boolean) => void;
  vatPct: number;
  setVatPct: (pct: number) => void;
  inputVatSelected: boolean;
  setInputVatSelected: (selected: boolean) => void;
  
  // Duration data
  displayDays: number | null;
  setDisplayDays: (days: number | null) => void;
  displayHours: number | null;
  setDisplayHours: (hours: number | null) => void;
  
  // Lines data
  lines: DocumentLineInput[];
  setLines: React.Dispatch<React.SetStateAction<DocumentLineInput[]>>;
  
  // Lookup data
  materials: Array<{ id: string; name: string; default_unit?: string | null; default_cost: number }>;
  equipment: Array<{ id: string; name: string; default_unit?: string | null; default_cost: number }>;
  labourRates: Array<{ id: string; name: string; rate_type: 'hour' | 'day'; rate: number }>;
  
  // Organization data
  orgs: Array<{ id: string; name: string; code: string }>;
  organisationId: string;
  setOrganisationId: (id: string) => void;
  
  // Customer store
  customers: Customer[];
  customersLoading: boolean;
  
  // Project data
  projectData: any;
  
  // Loading and error states
  loading: boolean;
  error: string | null;
  
  // Reference editing
  isEditingRef: boolean;
  setIsEditingRef: (editing: boolean) => void;
  refManuallyEdited: boolean;
  setRefManuallyEdited: (edited: boolean) => void;
}

// Shared line manipulation functions
export const useLineManipulation = (
  lines: DocumentLineInput[],
  setLines: React.Dispatch<React.SetStateAction<DocumentLineInput[]>>
) => {
  const removeLine = (idx: number) => {
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  const updateLineQty = (idx: number, qty: number) => {
    setLines(prev => prev.map((l, i) => i === idx ? {
      ...l,
      qty,
      lineTotal: (qty || 0) * (l.unitPrice || 0),
    } : l));
  };

  const changeQtyBy = (idx: number, delta: number) => {
    setLines(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const next = Math.max(0, Number(l.qty || 0) + delta);
      return { ...l, qty: next, lineTotal: (next || 0) * (l.unitPrice || 0) };
    }));
  };

  return { removeLine, updateLineQty, changeQtyBy };
};

// Shared presentational components
export const Row: React.FC<{ label: React.ReactNode; value: string; bold?: boolean; large?: boolean; sx?: any }> = ({ label, value, bold, large, sx }) => (
  <Stack direction="row" justifyContent="space-between" sx={sx}>
    <Typography level={bold ? 'title-sm' : 'body-sm'}>{label}</Typography>
    <Typography level={large ? 'title-lg' : bold ? 'title-sm' : 'body-sm'} fontWeight={bold ? 'lg' : 'md'}>{value}</Typography>
  </Stack>
);

export const FeeRow: React.FC<{ label: string; value: string; onClick?: () => void; clickable?: boolean }> = ({ label, value, onClick, clickable }) => (
  <Sheet variant="soft" color="neutral" sx={{ p: 1, borderRadius: 6, my: 0.5, cursor: clickable ? 'pointer' : 'default' }} onClick={onClick}>
    <Row label={label} value={value} />
  </Sheet>
);
